import { retryFileOperation } from '@risksmart-app/components/src/file/retryFileOperation';
import {
  pickActionableFileFailure,
  settleFileOperations,
} from '@risksmart-app/components/src/file/settleFileOperations';
import type {
  FileUploadResponse,
  UseFileUpload,
} from '@risksmart-app/components/src/file/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { isAxiosError } from 'axios';

import { useTRPC } from '../utils/trpc';

// Bare axios client for the presigned S3 PUT: no app Authorization header and
// no Sentry response interceptor, so a failed upload can't ship the signed URL
// (signature, credential params) to an error tracker. Mirrors the web twin
// (`packages/web/src/hooks/mutations/file/useFileUploadTRPC.tsx`).
const s3Client = axios.create();

// S3 validates the Content-Type against the signature.
const uploadToS3 = async (presignedUrl: string, file: File) => {
  return await s3Client.put(presignedUrl, file, {
    headers: {
      'Content-Type': file.type,
    },
  });
};

/**
 * tRPC twin of `@risksmart-app/components/src/file/useFileUpload` (V2 REST).
 * Same callable signature and lifecycle: presign registers PENDING, then S3
 * PUT and per-file save complete AVAILABLE. Each operation gets the shared
 * bounded retry policy; terminal failure leaves minted pending rows for the
 * orphan sweep. The portal only attaches files to one questionnaire-response
 * parent (no change-request path), so the web twin's change-request operation
 * is omitted here.
 *
 * Consumers must import the flag-gated `useFileUpdate` wrapper, never this.
 */
export const useFileUploadTRPC = (): UseFileUpload => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const presignMutation = useMutation({
    ...trpc.frontend.files.createPresignedUploadUrls.mutationOptions(),
  });
  const saveMutation = useMutation({
    ...trpc.frontend.files.save.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.files.getById.queryKey(),
        });
      },
    }),
  });

  return async ({
    parentType,
    parentIds,
    newFiles = [],
  }): Promise<FileUploadResponse> => {
    try {
      const presignedUrls =
        newFiles.length > 0
          ? await retryFileOperation(() =>
              presignMutation.mutateAsync({
                parentType,
                fileNames: newFiles.map((f) => f.name),
              })
            )
          : [];
      const newFilePutPromises = presignedUrls.map(async (url, index) => {
        const file = newFiles[index];
        await retryFileOperation(() => uploadToS3(url.signedUrl, file));

        await retryFileOperation(() =>
          saveMutation.mutateAsync({
            ParentIds: parentIds,
            ParentType: parentType,
            Files: [
              {
                FileId: url.key,
                FileName: file.name,
                FileSize: file.size,
                ContentType: file.type,
                Meta: file.meta,
                ChangeRequestFileOperation: null,
              },
            ],
          })
        );

        return url.key;
      });

      const settled = await settleFileOperations(newFilePutPromises);
      if (settled.failures.length > 0) {
        throw pickActionableFileFailure(settled.failures);
      }

      return { fileIds: settled.values };
    } catch (error) {
      // V2 parity: mask raw axios messages (the S3 PUT failure). tRPC errors
      // pass through untouched so downstream error handling can read them.
      if (isAxiosError(error)) {
        throw new Error('Something went wrong');
      }
      throw error;
    }
  };
};
